import { useEffect, useState } from "react";
import { Check, Pencil, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { rpc } from "../../lib/rpc";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";

/**
 * KategorienDialog -- Kategorien anlegen und umbenennen (#44 Punkt 2).
 * Löschen bewusst nicht angeboten (ON DELETE SET NULL würde Artikel
 * stillschweigend entkategorisieren).
 *
 * onChanged() feuert nach jeder erfolgreichen Änderung, damit der Parent
 * seine Kategorienliste neu lädt.
 */
export function KategorienDialog({ open, onClose, categories = [], onChanged, testId = "kategorien-dialog" }) {
    const [newName, setNewName] = useState("");
    const [editId, setEditId] = useState(null);
    const [editName, setEditName] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) {
            setNewName("");
            setEditId(null);
            setEditName("");
        }
    }, [open]);

    const create = async () => {
        const name = newName.trim();
        if (!name) return toast.error("Name Pflicht.");
        setBusy(true);
        const res = await rpc.post("/master/categories", { name });
        setBusy(false);
        if (!res.ok) return toast.error(res.error);
        toast.success(`Kategorie „${name}" angelegt.`);
        setNewName("");
        onChanged?.(res.data);
    };

    const startEdit = (c) => {
        setEditId(c.id);
        setEditName(c.name);
    };

    const saveEdit = async () => {
        const name = editName.trim();
        if (!name) return toast.error("Name Pflicht.");
        const current = categories.find((c) => c.id === editId);
        if (current && current.name === name) {
            setEditId(null);
            return;
        }
        setBusy(true);
        const res = await rpc.patch(`/master/categories/${editId}`, { name });
        setBusy(false);
        if (!res.ok) return toast.error(res.error);
        toast.success("Kategorie umbenannt.");
        setEditId(null);
        onChanged?.(res.data);
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent data-testid={testId}>
                <DialogHeader>
                    <DialogTitle>Kategorien</DialogTitle>
                    <DialogDescription className="text-xs">
                        Anlegen und umbenennen. Artikel behalten ihre Zuordnung beim Umbenennen.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex items-center gap-2">
                    <Input
                        placeholder="Neue Kategorie"
                        aria-label="Neue Kategorie"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && create()}
                        data-testid={`${testId}-new-name`}
                    />
                    <Button onClick={create} disabled={busy || !newName.trim()} data-testid={`${testId}-create`}>
                        <Plus className="mr-1 h-4 w-4" /> Anlegen
                    </Button>
                </div>

                <div className="max-h-80 overflow-auto rounded-md border border-border/60 bg-card/30">
                    {categories.length === 0 ? (
                        <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                            Noch keine Kategorien.
                        </div>
                    ) : (
                        <ul className="divide-y divide-border/40" data-testid={`${testId}-list`}>
                            {categories.map((c) => (
                                <li
                                    key={c.id}
                                    className="flex items-center gap-2 px-3 py-2 text-sm"
                                    data-testid={`${testId}-row-${c.id}`}
                                >
                                    {editId === c.id ? (
                                        <>
                                            <Input
                                                value={editName}
                                                onChange={(e) => setEditName(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") saveEdit();
                                                    if (e.key === "Escape") setEditId(null);
                                                }}
                                                autoFocus
                                                className="h-8"
                                                aria-label="Kategorie umbenennen"
                                                data-testid={`${testId}-edit-name`}
                                            />
                                            <Button size="sm" onClick={saveEdit} disabled={busy} aria-label="Speichern" data-testid={`${testId}-edit-save`}>
                                                <Check className="h-4 w-4" />
                                            </Button>
                                            <Button size="sm" variant="ghost" onClick={() => setEditId(null)} aria-label="Abbrechen">
                                                <X className="h-4 w-4" />
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            <span className="flex-1 truncate">{c.name}</span>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => startEdit(c)}
                                                aria-label={`${c.name} umbenennen`}
                                                data-testid={`${testId}-edit-${c.id}`}
                                            >
                                                <Pencil className="h-4 w-4" />
                                            </Button>
                                        </>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="flex justify-end">
                    <Button variant="ghost" onClick={() => onClose?.()} data-testid={`${testId}-close`}>
                        Schließen
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
